import { useState, useEffect, useCallback } from 'react';
import Web3 from 'web3';
import { _SGBF_ERC721, _AuctionManager } from '../../abi';
import { chainData } from '../../chains';
import { metaCache, logger } from '../../utils';
import { getCollectionById, getNFTMetadata } from '../../lib/api';

interface InitialData {
    initialCollectionInfo?: any | null;
    initialTokenData?: any | null;
}

interface TokenInfo {
    metadata: any;
    img: string;
    owner: string | null;
    auctionData: any | null;
    contractOwner: string | null;
    tid: string;
    contractAddress: string;
    unminted: boolean;
}

interface UseItemDetailsReturn {
    collectionInfo: any | null;
    tokenInfo: TokenInfo | null;
    isLoading: boolean;
    error: string | null;
    refetch: () => void;
}

/**
 * Custom hook for ItemDetails page.
 *
 * Fetches and manages NFT item details including:
 * - Collection metadata
 * - Token metadata (JSON)
 * - Current owner (from blockchain)
 * - Auction data (if listed)
 * - Contract owner
 *
 * Uses Web3 to query ERC-721 contract directly and Backend for metadata caching.
 * Handles loading states and errors.
 *
 * @param contractId - Collection ID in database
 * @param tokenId - Token/edition number
 * @param initialData - Optional preloaded initial data from route loader
 * @returns Item details and state
 */
export const useItemDetails = (
    contractId: string,
    tokenId: string,
    initialData: InitialData = {}
): UseItemDetailsReturn => {
    const { initialCollectionInfo, initialTokenData } = initialData;

    const [collectionInfo, setCollectionInfo] = useState<any | null>(initialCollectionInfo || null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Initialize tokenInfo synchronously from initialTokenData for SSR and instant UI
    const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(() => {
        if (initialTokenData) {
            try {
                const metadata = typeof initialTokenData.metadata === 'string'
                    ? JSON.parse(initialTokenData.metadata)
                    : initialTokenData.metadata;

                if (metadata) {
                    return {
                        metadata,
                        img: metadata.image || initialTokenData.img_ipfs || initialTokenData.image || '',
                        owner: null, // Will be fetched via Web3
                        auctionData: null, // Will be fetched via Web3
                        contractOwner: null, // Will be fetched via Web3
                        tid: tokenId,
                        contractAddress: contractId,
                        unminted: true, // Unknown until we check owner
                    };
                }
            } catch (e) {
                console.warn('Failed to parse initial token metadata:', e);
            }
        }
        return null;
    });

    const fetchItemDetails = useCallback(async () => {
        // Skip Web3 calls on server
        if (typeof window === 'undefined') {
            setIsLoading(false);
            return;
        }

        if (!contractId || !tokenId) {
            setIsLoading(false);
            return;
        }

        // Only fetch Web3 data if we have collection info (needed for contract address and chain)
        const collection = collectionInfo || initialCollectionInfo;
        if (!collection) {
            // Can't fetch without collection info, but tokenInfo might already be set from initial data
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const chainId = collection.network || 2; // Default to Songbird
            const provider = new Web3.providers.HttpProvider(chainData[chainId].rpc);
            const web3 = new Web3(provider);
            const erc721 = new web3.eth.Contract(_SGBF_ERC721, collection.contractAddress);

            // Fetch owner and auction data via Web3
            let owner: string | null = null;
            let auctionData: any = null;
            const AMContractAddress = chainData[chainId].aManager;
            try {
                owner = await erc721.methods.ownerOf(tokenId).call();
                if (owner === AMContractAddress) {
                    const amc = new web3.eth.Contract(_AuctionManager, AMContractAddress);
                    auctionData = await amc.methods.getAuctionData(collection.contractAddress, tokenId).call();
                    owner = auctionData.ownerAddress;
                }
            } catch (e) {
                owner = null;
            }

            // Get contract owner
            let contractOwner: string | null = null;
            try {
                contractOwner = await erc721.methods.owner().call();
            } catch (e) {
                contractOwner = null;
            }

            // Update tokenInfo with Web3 data (preserving existing metadata)
            setTokenInfo(prev => {
                if (!prev) return null;
                return {
                    ...prev,
                    owner,
                    auctionData,
                    contractOwner,
                    unminted: !owner,
                };
            });

        } catch (err: any) {
            logger("Error in useItemDetails (Web3 fetch):", err);
            // Don't set error as this is just for enriching existing data
            console.warn('Web3 fetch failed, continuing with limited data:', err);
        } finally {
            setIsLoading(false);
        }
    }, [contractId, tokenId, initialCollectionInfo, collectionInfo]);

    useEffect(() => {
        fetchItemDetails();
    }, [fetchItemDetails]);

    return { collectionInfo, tokenInfo, isLoading, error, refetch: fetchItemDetails };
};

export default useItemDetails;
